import type {ReactElement} from 'react';
import React, {memo, useEffect, useMemo} from 'react';
import type {ItemDefinition, RowDefinition} from '@Libraries/timeline/types';
import {useTimelineContext} from '@Libraries/timeline';
import {groupItemsToSubrows} from '@Libraries/timeline/utils';
import {TimelineAudioItem} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-audio-item/timeline-audio-item';
import {TimelineDesignSeekItem} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-design-seek-item/timeline-design-seek-item';
import {updateTimelineContentHeight} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {getPosterDuration} from '@Components/poster-editor/poster-editor-reducer';
import {TimelineItemTypeId} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.helper';
import {BaseTimelineRow} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-row/base-timeline-row';
import {BaseTimelineSubrow} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-subrow/base-timeline-subrow';
import {TimelineSubtitleItem} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-subtitle-item/timeline-subtitle-item';
import styles from './base-timeline-content.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {TIME_LINE_LEFT_MARGIN} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline/timeline.types';

interface TimelineInnerContentProps extends RowDefinition {
  rows: RowDefinition[];
  items: ItemDefinition[];
}

function BaseTimelineContent({...props}: TimelineInnerContentProps): ReactElement<any> | null {
  const {setTimelineRef, style, timelineRef, range} = useTimelineContext();
  const posterDuration = useAppSelector(getPosterDuration);
  const widthPerSecond = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.widthPerSecond;
  });
  const isPanning = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isPanning;
  });
  const isDesignItemResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isDesignResizing;
  });
  const selectedAudioItemId = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.selectedAudioItemUID;
  });
  const dispatch = useAppDispatch();

  useEffect((): void => {
    if (timelineRef.current) {
      dispatch(updateTimelineContentHeight(timelineRef.current.scrollHeight));
    }
  }, [props.rows]);

  const getWidthStyle = (): string | undefined => {
    return `${widthPerSecond && posterDuration ? widthPerSecond * posterDuration : undefined}px`;
  };

  const groupedSubrows = useMemo(() => {
    return groupItemsToSubrows(props.items, range);
  }, [props.items, range]);
  const getItemsFromId = (item: ItemDefinition, isLastItem: boolean): ReactElement<any> | null => {
    const rowId = item.rowId as TimelineItemTypeId;
    const filteredRowId = rowId.split('_')[0] as TimelineItemTypeId;

    switch (filteredRowId) {
      case TimelineItemTypeId.Audio:
        return <TimelineAudioItem isLastItem={isLastItem} id={item.id} key={item.id} range={item.span} />;
      case TimelineItemTypeId.Design:
        return <TimelineDesignSeekItem isLastItem={isLastItem} id={item.id} key={item.id} range={item.span} />;
      case TimelineItemTypeId.Transcript:
        return (
          <TimelineSubtitleItem
            isDisplayOnly={item?.data?.isDisplayOnly}
            transcriptId={item?.data?.transcriptId}
            id={item.id}
            key={item.id}
            range={item.span}
          >{`Item ${item.id}`}</TimelineSubtitleItem>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`${styles.container} spacing-p-t-2 ${isPanning || isDesignItemResizing ? '' : styles.hasTransition}`}
      ref={setTimelineRef}
      style={{
        ...style,
        marginLeft: `${TIME_LINE_LEFT_MARGIN}px`,
        width: getWidthStyle(),
        overflow: 'visible',
      }}
    >
      {props.rows.map((row) => {
        return (
          <BaseTimelineRow id={row.id} key={row.id}>
            {groupedSubrows[row.id]?.map((subrow, index) => {
              let isSelectedInSubRow = false;
              const itemIndex = `item-${index}`;
              return (
                <BaseTimelineSubrow id={row.id} key={`${row.id}-${itemIndex}`}>
                  {subrow.map((item, subrowIndex) => {
                    if (item.id === selectedAudioItemId || row.id === String(TimelineItemTypeId.Design)) {
                      isSelectedInSubRow = true;
                    }
                    return getItemsFromId(item, isSelectedInSubRow && subrowIndex === subrow.length - 1);
                  })}
                </BaseTimelineSubrow>
              );
            })}
          </BaseTimelineRow>
        );
      })}
    </div>
  );
}

export default memo(BaseTimelineContent);
